import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import LoadingScreen from '../../components/LoadingScreen';
import { 
    AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
    ComposedChart, Line,
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer 
} from 'recharts';
import { 
    FaUsers, FaBox, FaTags, FaGift, FaShoppingCart, FaCheckCircle, 
    FaTruck, FaTimesCircle, FaChartLine, FaRupeeSign, FaCalendarAlt, FaClock, FaCog 
} from 'react-icons/fa';

function AdminHomePage() {
  const [counts, setCounts] = useState({
    usersCount: 0,
    categoriesCount: 0,
    productsCount: 0,
    brandsCount: 0,
    offersCount: 0,
    ordersCount: 0,
    deliveredOrdersCount: 0,
    pendingOrdersCount: 0,
    confirmedOrdersCount: 0,
    shippedOrdersCount: 0,
    cancelledOrdersCount: 0
  });

  const [salesData, setSalesData] = useState({
    dailySales: [],
    monthlySales: [],
    salesSummary: {},
    topProducts: [],
    orderStatusDistribution: []
  });

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('daily');

  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('authToken');
      if (!token) {
        toast.error("Unauthorized access! Redirecting...");
        setTimeout(() => navigate('/admin-login'), 2000);
        return;
      }
      try {
        setLoading(true);
        const baseURL = API_URL || 'http://localhost:3500';
        const [
          countsResponse,
          dailySalesResponse,
          monthlySalesResponse,
          salesSummaryResponse,
          topProductsResponse,
          orderStatusResponse
        ] = await Promise.all([
          axios.get(`${baseURL}/counts`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${baseURL}/daily-sales`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${baseURL}/monthly-sales`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${baseURL}/sales-summary`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${baseURL}/top-products`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${baseURL}/order-status-distribution`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        setCounts(countsResponse.data || {});
        setSalesData({
          dailySales: dailySalesResponse.data || [],
          monthlySales: monthlySalesResponse.data || [],
          salesSummary: salesSummaryResponse.data || {},
          topProducts: topProductsResponse.data || [],
          orderStatusDistribution: orderStatusResponse.data || []
        });
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        toast.error("Failed to fetch dashboard data. Try again later.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#FFA500',
      accepted: '#007BFF',
      shipped: '#17A2B8',
      delivered: '#28A745',
      cancelled: '#DC3545'
    };
    return colors[status] || '#6C757D';
  };


  return (
    <div className="container-fluid p-3 pt-2 g-0 adma">
      <div className="row ">
        <div className="col-12">
          <div className="section-title">
            <h4 className='mt-1'>KIPPS SWEETS - ADMIN DASHBOARD</h4>
            <p className="text-muted p-0 m-0">Welcome back! Here's what's happening with your business today.</p>
          </div>
        </div>
      </div>

      {loading ? (
        <LoadingScreen message="Loading dashboard data..." />
      ) : (<>
      <div className="row g-0 admin-quick-links-row">
        <div className="col">
          <Link to="/admin/orders" className="quick-link-card ql-orders">
            <FaShoppingCart />
            <span>Orders</span>
          </Link>
        </div>
        <div className="col">
          <Link to="/admin/pending-orders" className="quick-link-card ql-pending">
            <FaClock />
            <span>Pending</span>
          </Link>
        </div>
        <div className="col">
          <Link to="/admin/products" className="quick-link-card ql-products">
            <FaBox />
            <span>Products</span>
          </Link>
        </div>
        <div className="col">
          <Link to="/admin/categories" className="quick-link-card ql-categories">
            <FaTags />
            <span>Categories</span>
          </Link>
        </div>
        <div className="col">
          <Link to="/admin/users" className="quick-link-card ql-customers">
            <FaUsers />
            <span>Customers</span>
          </Link>
        </div>
        <div className="col">
          <Link to="/admin/brands" className="quick-link-card ql-brands">
            <FaTags />
            <span>Brands</span>
          </Link>
        </div>
        <div className="col">
          <Link to="/admin/offers" className="quick-link-card ql-offers">
            <FaGift />
            <span>Offers</span>
          </Link>
        </div>
        <div className="col">
          <Link to="/admin/settings" className="quick-link-card ql-settings">
            <FaCog />
            <span>Settings</span>
          </Link>
        </div>
      </div>
      <div className="row g-0 dashboard-kpis-row">
        <div className="col-lg-3 col-md-6 col-sm-12">
          <div className="card h-100">
            <div className="card-body">
              <h6>Today's Sales</h6>
              <h3>{formatCurrency(salesData.salesSummary.todaySales || 0)}</h3>
              <small>{salesData.salesSummary.todayOrders || 0} orders today</small>
            </div>
          </div>
        </div>
        <div className="col-lg-3 col-md-6 col-sm-12">
          <div className="card h-100">
            <div className="card-body">
              <h6>This Week</h6>
              <h3>{formatCurrency(salesData.salesSummary.weekSales || 0)}</h3>
              <small>{salesData.salesSummary.weekOrders || 0} orders this week</small>
            </div>
          </div>
        </div>
        <div className="col-lg-3 col-md-6 col-sm-12">
          <div className="card h-100">
            <div className="card-body">
              <h6>This Month</h6>
              <h3>{formatCurrency(salesData.salesSummary.monthSales || 0)}</h3>
              <small>{salesData.salesSummary.monthOrders || 0} orders this month</small>
            </div>
          </div>
        </div>
        <div className="col-lg-3 col-md-6 col-sm-12">
          <div className="card h-100">
            <div className="card-body">
              <h6>Total Sales</h6>
              <h3>{formatCurrency(salesData.salesSummary.totalSales || 0)}</h3>
              <small>{salesData.salesSummary.totalOrders || 0} total orders</small>
            </div>
          </div>
        </div>
      </div>
      <div className="row dashboard-charts-row">
        <div className="col-lg-8 col-md-12">
          <div className="card sales-analytics-card">
            <div className="card-header">
              <h5 className="mb-0">Sales Performance</h5>
              <div className="btn-group analytics-tab-group" role="group">
                <button
                  type="button"
                  className={`btn btn-sm ${activeTab === 'daily' ? 'btn-danger' : 'btn-outline-danger'}`}
                  onClick={() => setActiveTab('daily')}
                >Daily</button>
                <button
                  type="button"
                  className={`btn btn-sm ${activeTab === 'monthly' ? 'btn-danger' : 'btn-outline-danger'}`}
                  onClick={() => setActiveTab('monthly')}
                >Monthly</button>
              </div>
            </div>
            <div className="card-body pt-2">
              <div className="row small-kpi-row">
                {(() => {
                  const daily = salesData.salesSummary.todaySales || 0;
                  const yesterday = salesData.dailySales?.slice(-2)[0]?.sales && salesData.dailySales?.slice(-2)[0]?.date !== salesData.dailySales?.slice(-1)[0]?.date ? salesData.dailySales?.slice(-2)[0]?.sales : salesData.dailySales?.slice(-2)[0]?.sales || 0; // basic fallback
                  const growth = yesterday ? (((daily - yesterday) / yesterday) * 100).toFixed(1) : '0.0';
                  const avgOrderVal = salesData.salesSummary.todayOrders ? daily / salesData.salesSummary.todayOrders : 0;
                  const monthSales = salesData.salesSummary.monthSales || 0;
                  const monthOrders = salesData.salesSummary.monthOrders || 0;
                  const avgMonthly = monthOrders ? monthSales / monthOrders : 0;
                  return (
                    <>
                      <div className="col-6 col-xl-3">
                        <div className="mini-kpi">
                          <span className="mini-kpi-label">Today AOV</span>
                          <strong className="mini-kpi-value">{formatCurrency(avgOrderVal || 0)}</strong>
                        </div>
                      </div>
                      <div className="col-6 col-xl-3">
                        <div className="mini-kpi">
                          <span className="mini-kpi-label">Daily Growth</span>
                          <strong className={`mini-kpi-value ${growth >= 0 ? 'text-success' : 'text-danger'}`}>{growth}%</strong>
                        </div>
                      </div>
                      <div className="col-6 col-xl-3">
                        <div className="mini-kpi">
                          <span className="mini-kpi-label">Month AOV</span>
                          <strong className="mini-kpi-value">{formatCurrency(avgMonthly || 0)}</strong>
                        </div>
                      </div>
                      <div className="col-6 col-xl-3">
                        <div className="mini-kpi">
                          <span className="mini-kpi-label">Orders Today</span>
                          <strong className="mini-kpi-value">{salesData.salesSummary.todayOrders || 0}</strong>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
              <ResponsiveContainer width="100%" height={320}>
                {activeTab === 'daily' ? (
                  <AreaChart data={salesData.dailySales} margin={{ top: 20, right: 28, left: 5, bottom: 15 }}>
                    <defs>
                      <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#ed3237" stopOpacity={0.7}/>
                        <stop offset="95%" stopColor="#ed3237" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f1" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={{ stroke: '#cbd5e1' }} dy={5} />
                    <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={false} dx={-3} width={50} />
                    <Tooltip content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        return (
                          <div className="chart-tooltip shadow-sm">
                            <div className="ct-title">{label}</div>
                            <div className="ct-row"><span>Sales:</span><strong>{formatCurrency(payload[0].value)}</strong></div>
                          </div>
                        );
                      }
                      return null;
                    }} />
                    <Area type="monotone" dataKey="sales" stroke="#ed3237" strokeWidth={2} fillOpacity={1} fill="url(#colorSales)" />
                  </AreaChart>
                ) : (
                  <ComposedChart data={salesData.monthlySales} margin={{ top: 20, right: 28, left: 5, bottom: 15 }}>
                    <defs>
                      <linearGradient id="colorBarSales" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#ed3237" stopOpacity={0.85}/>
                        <stop offset="100%" stopColor="#f87171" stopOpacity={0.4}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f1" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={{ stroke: '#cbd5e1' }} dy={5} />
                    <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={false} dx={-3} width={50} />
                    <Tooltip content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        return (
                          <div className="chart-tooltip shadow-sm">
                            <div className="ct-title">{label}</div>
                            <div className="ct-row"><span>Sales:</span><strong>{formatCurrency(payload[0].value)}</strong></div>
                          </div>
                        );
                      }
                      return null;
                    }} />
                    <Bar dataKey="sales" radius={[5, 5, 0, 0]} fill="url(#colorBarSales)" maxBarSize={64} />
                    <Line type="monotone" dataKey="sales" stroke="#ed3237" strokeWidth={2} dot={false} strokeDasharray="5 3" />
                  </ComposedChart>
                )}
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        <div className="col-lg-4 col-md-12">
          <div className="card order-status-card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Order Status Mix</h5>
              <span className="badge">{counts.ordersCount || 0} Orders</span>
            </div>
             <div className="card-body">
               {salesData.orderStatusDistribution.map((s, i) => {
                 const pct = counts.ordersCount ? ((s.count / counts.ordersCount) * 100).toFixed(1) : 0;
                 return (
                   <div className="status-row" key={s.status + i}>
                     <div className="d-flex align-items-center">
                       <span className="status-dot me-3" style={{ backgroundColor: getStatusColor(s.status), width: '8px', height: '8px' }}></span>
                       <div>
                         <span className="status-row-label">{s.status}</span>
                         <span className="status-row-count">{s.count} {s.count === 1 ? 'order' : 'orders'}</span>
                       </div>
                     </div>
                     <strong className="status-row-value">{pct}%</strong>
                   </div>
                 );
               })}
             </div>
          </div>
        </div>
      </div>
      <div className="row dashboard-tp-row">
        <div className="col-12">
          <div className="top-products-card">
            <div className="tp-card-header">
              <div>
                <h5 className="tp-card-title">Top Selling Products</h5>
                <span className="tp-card-sub">By units sold this period</span>
              </div>
              <div className="d-flex gap-2 align-items-center">
                {salesData.topProducts.length > 0 && (
                  <button type="button" className="tp-export-btn" onClick={() => {
                    const rows = [['Rank','Product','Quantity','Contribution %']];
                    const total = salesData.topProducts.reduce((s,p)=> s + (p.quantity||0),0) || 1;
                    salesData.topProducts.forEach((p,i)=> rows.push([i+1, p.name, p.quantity, ((p.quantity/total)*100).toFixed(2)]));
                    const csv = rows.map(r => r.map(v => '"'+ String(v).replace(/"/g,'""') +'"').join(',')).join('\n');
                    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url; a.download = 'top_products.csv'; a.click(); URL.revokeObjectURL(url);
                  }}>↓ Export CSV</button>
                )}
              </div>
            </div>

            {/* Column header row */}
            <div className="tp-col-header">
              <span className="tp-col-rank">#</span>
              <span className="tp-col-name">Product</span>
              <span className="tp-col-bar">Contribution</span>
              <span className="tp-col-qty">Units</span>
              <span className="tp-col-pct">Share</span>
            </div>

            {salesData.topProducts.length > 0 ? (
              <div className="tp-list" role="list">
                {(() => {
                  const total = salesData.topProducts.reduce((s,p)=> s + (p.quantity||0),0) || 1;
                  const RANK_COLORS = ['#f59e0b', '#94a3b8', '#cd7c2f', '#64748b', '#64748b'];
                  return salesData.topProducts.map((product, index) => {
                    const pct = ((product.quantity / total) * 100) || 0;
                    return (
                      <div className="tp-item" role="listitem" key={index}>
                        <span className="tp-rank">
                          {index + 1}
                        </span>
                        <span className="tp-name" title={product.name}>{product.name}</span>
                        <div className="tp-bar-cell">
                          <div className="tp-track">
                            <div
                              className="tp-fill"
                              style={{
                                width: pct + '%',
                                backgroundColor: index === 0 ? '#ed3237' : index === 1 ? '#f97316' : index === 2 ? '#eab308' : '#94a3b8'
                              }}
                            ></div>
                          </div>
                        </div>
                        <span className="tp-qty-val">{product.quantity}</span>
                        <span className="tp-pct-val">{pct.toFixed(1)}%</span>
                      </div>
                    );
                  });
                })()}
              </div>
            ) : (
              <p className="text-muted text-center py-4 m-0">No sales data available</p>
            )}


          </div>
        </div>
      </div>
      </>)}
    </div>
  );
}

export default AdminHomePage; 